import React, { useState, useEffect } from 'react';
import {
	Box,
	Image,
	Text,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	ModalCloseButton,
	useDisclosure,
	Input,
	VStack,
	Button,
	Spinner,
	Skeleton,
	useBreakpointValue,
	useColorModeValue
} from '@chakra-ui/react';
import { FiUploadCloud } from 'react-icons/fi';
import { saveAs } from 'file-saver';
import axiosInstance from '../../../utils/axiosInstance';
import axios from "axios";
import { useLocation } from 'react-router-dom'; // Import useLocation


const MediaComponent = ({ userId, booking, isEditable }) => {
	const [media, setMedia] = useState({ photos: [], videos: [] });
	const [newlySelectedFiles, setNewlySelectedFiles] = useState({ photos: [], videos: [] });
	const [showUpload, setShowUpload] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [signedUrls, setSignedUrls] = useState([]);
	const [error, setError] = useState('');
	const [overlayMedia, setOverlayMedia] = useState(null); // For custom overlay with display file
	const bgColor = useColorModeValue('white', 'black');
	const spinnerColor = useColorModeValue('black', 'white');
	const columnSize = useBreakpointValue({ base: '100px', md: '150px' });
	const [loading, setLoading] = useState(true); // Initialize loading state
	const { isOpen, onOpen, onClose } = useDisclosure();
	const location = useLocation(); // Get the current location
	const isUserPage = location.pathname.includes('/user'); // Check if "/user" is in the path
	const [imageLoaded, setImageLoaded] = useState(false); // ✅ Safe if you extract this into a component per image



	// Fetch media files from the backend
	useEffect(() => {
		const fetchMediaFiles = async () => {
			setLoading(true);
			try {
				const backendUrl = process.env.NODE_ENV === 'production'
					? process.env.REACT_APP_BACKEND_URL
					: 'http://localhost:8080';
				let response;
				if (!userId) {
					response = await axios.get(`${backendUrl}/booking/media/list/${booking.booking_id}`, {
						params: {
							customerId: booking.customer_id,
							photographerId: booking.photographer_id,
						}
					});
				} else {
					response = await axios.get(`${backendUrl}/media/list/${userId}`);
				}
				const mediaFiles = response.data.mediaFiles || [];
				const photos = mediaFiles.filter(file => file.contentType.startsWith('image/')).map(file => ({
					...file,
					displayUrl: file.compressedUrl || file.url, // Use server URL for existing files
				}));
				const videos = mediaFiles.filter(file => file.contentType.startsWith('video/')).map(file => ({
					...file,
					displayUrl: file.url, // Use server URL for existing files
				}));
				setMedia({ photos, videos });
			} catch (err) {
				console.error('Error fetching media files:', err);
				setError('Failed to retrieve media files. Please try again.');
			} finally {
				setLoading(false);
			}
		};

		fetchMediaFiles();
	}, [userId]);

	// Open the modal when overlayMedia is set
	useEffect(() => {
		if (overlayMedia) {
			onOpen();
		}
	}, [overlayMedia, onOpen]);

	const handleFileChange = (e) => {
		const files = Array.from(e.target.files);
		console.log(files)
		// Separate new photos and videos based on file type and create preview URLs
		const selectedPhotos = files.filter(file => file.type.startsWith('image/')).map(file => ({
			name: file.name,
			type: file.type,
			compressedUrl: URL.createObjectURL(file),
			file,
		}));
		const selectedVideos = files.filter(file => file.type.startsWith('video/')).map(file => ({
			name: file.name,
			type: file.type,
			displayUrl: URL.createObjectURL(file),
			file,
		}));

		// Append new files to `newlySelectedFiles` without overwriting existing ones
		setNewlySelectedFiles(prev => ({
			photos: [...prev.photos, ...selectedPhotos],
			videos: [...prev.videos, ...selectedVideos],
		}));

		// Update media for immediate preview
		setMedia(prev => ({
			photos: [...prev.photos, ...selectedPhotos],
			videos: [...prev.videos, ...selectedVideos],
		}));

		setShowUpload(true); // Show upload button once files are selected
	};

	const handleUpload = async () => {
		try {
			setIsUploading(true); // Show spinner
			// Prepare metadata for signed URL request
			const fileData = newlySelectedFiles.photos.concat(newlySelectedFiles.videos).map(file => ({
				name: file.name,
				type: file.type,
			}));
			console.log("FILEDATA", fileData);

			const backendUrl = process.env.NODE_ENV === 'production'
				? process.env.REACT_APP_BACKEND_URL
				: 'http://localhost:8080';

			// Request signed URLs for the files to upload
			const response = !userId
				? await axiosInstance.put(`${backendUrl}/booking/media/upload/${booking.booking_id}`, {
					files: fileData,
					params: { customerId: booking.customer_id, photographerId: booking.photographer_id },
				})
				: await axiosInstance.put(`${backendUrl}/media/upload`, { files: fileData });

			const { signedUrls } = response.data;
			console.log("Received signed URLs:", signedUrls);  // Debug log

			// Upload each file to its respective signed URL
			const uploadPromises = newlySelectedFiles.photos.concat(newlySelectedFiles.videos).map(fileObj => {
				const signedUrlObj = signedUrls.find(url =>
					url.fileName.toLowerCase() === fileObj.name.toLowerCase()
				);

				console.log("Uploading:", fileObj.name); // Debug log
				if (!signedUrlObj) {
					throw new Error(`No signed URL found for file: ${fileObj.name}`);
				}

				// Upload to the signed URL
				return axios.put(signedUrlObj.uploadUrl, fileObj.file, {
					headers: { 'Content-Type': fileObj.type },
				});
			});

			await Promise.all(uploadPromises);

			// Clear selected files and signed URLs after successful upload
			setNewlySelectedFiles({ photos: [], videos: [] });
			setSignedUrls([]);
			setIsUploading(false); // Hide spinner
			setShowUpload(false);

			console.log('All files uploaded successfully');
		} catch (err) {
			console.error('Error uploading files:', err);
			setError(`Failed to upload media files: ${err.message}`);
		}
	};


	useEffect(() => {
		setShowUpload(newlySelectedFiles.photos.length > 0 || newlySelectedFiles.videos.length > 0);
	}, [newlySelectedFiles]);


	function addFileExtension(filename, type) {
		// Define a mapping of MIME types to file extensions
		const mimeToExtension = {
			"image/jpeg": "jpg",
			//"image/png": "png",
			"image/gif": "gif",
			"image/webp": "webp",
			"image/bmp": "bmp",
			"image/tiff": "tiff",
			"video/mp4": "mp4",
			"video/quicktime": "mov",
			"video/webm": "webm",
			"application/pdf": "pdf",
			"text/plain": "txt",
			// Add additional MIME types as needed
		};

		// Strip existing extension if present
		const nameWithoutExtension = filename.replace(/\.[^/.]+$/i, '');

		// Look up the extension, defaulting to "bin" if the MIME type is unrecognized
		const extension = mimeToExtension[type] || "bin";

		// Return the filename with the appropriate extension
		console.log(`${nameWithoutExtension}.${extension}`)
		return `${nameWithoutExtension}.${extension}`;
	}

	const handleDownload = async (url, filename, type) => {
		try {
			console.log(overlayMedia)
			const response = await fetch(url);
			const blob = await response.blob();
			const file = new File([blob], addFileExtension(filename, type), {
				type: blob.type,
			});
			if (navigator.canShare && navigator.canShare({ files: [file] })) {
				await navigator.share({
					files: [file],
					title: "Download Image",
					text: "Here is the image you wanted to download.",
				});
				console.log("Image shared successfully");
			} else {
				// Fallback method for devices that do not support Web Share API
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.style.display = "none";
				a.href = url;
				a.download = filename;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(url);
				document.body.removeChild(a);
				console.log("Image downloaded using fallback method");
			}
		} catch (error) {
			console.error("Download failed:", error);
		}
	};

	// Close modal and reset overlayMedia
	const handleClose = () => {
		setOverlayMedia(null);
		onClose();
	};

	//console.log(overlayMedia)
	return (
		<Box p={4}>
			{isEditable && (
				<Box
					p={8}
					bg={bgColor}
					border="2px dashed"
					borderColor="gray.600"
					borderRadius="md"
					textAlign="center"
					onDrop={(e) => {
						e.preventDefault();
						handleFileChange(e);
					}}
					onDragOver={(e) => e.preventDefault()}
					cursor="pointer"
					onClick={() => document.getElementById('file-input').click()}
				>
					<VStack spacing={2}>
						<Input
							id="file-input"
							type="file"
							multiple
							accept="image/jpeg, image/jpg, image/gif, video/*"
							display="none"
							onChange={handleFileChange}
						/>
						<FiUploadCloud size={50} />
						<Text>Drag and drop here to upload</Text>
						<Text>.jpg, .mp4, .mov up to 5MB</Text>
					</VStack>
				</Box>
			)}
			{error && <Text color="red.500" mb={4}>{error}</Text>}

			{/* Photos Section */}
			<Text fontSize="xl" mb={4}>Photos</Text>
			{loading ? ( // Show skeleton loader while loading
				<Box
					display="grid"
					gridTemplateColumns="repeat(3, 1fr)"
					gap={2}
				>
					{[...Array(9)].map((_, index) => ( // Create 9 skeleton placeholders
						<Skeleton
							key={index}
							width="100%"
							pt="100%"
							borderRadius="md"
							boxShadow="lg"
						/>
					))}
				</Box>
			) : (
				<Box
					display="grid"
					gridTemplateColumns="repeat(3, 1fr)"
					gap={2}
				>
					{media.photos
  .filter((photo) => photo.compressedUrl) // Only Compressed Photos
  .map((photo, index) => {

    return (
      <Box
        key={index}
        width="100%"
        pt="100%"
        position="relative"
        cursor="pointer"
        borderRadius="md"
        overflow="hidden"
        boxShadow="lg"
        onClick={() => setOverlayMedia(photo)}
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Skeleton isLoaded={imageLoaded} width="100%" height="100%" borderRadius="8px">
            <Image
              src={photo.compressedUrl}
              alt={`photo-${index}`}
							width="100%"
              height="100%"
              objectFit="cover"
              borderRadius="8px"
              loading="lazy" // ✅ Lazy Load
              onLoad={() => setImageLoaded(true)} // ✅ Skeleton trigger
            />
          </Skeleton>
        </Box>
      </Box>
    );
  })}

				</Box>
			)}

			{/* Videos Section */}
			<Text fontSize="xl" mt={8} mb={4}>Videos</Text>
			{loading ? ( // Show skeleton loader while loading
				<Box
					display="grid"
					gridTemplateColumns="repeat(3, 1fr)"
					gap={4}
				>
					{[...Array(6)].map((_, index) => ( // Create 6 skeleton placeholders for videos
						<Skeleton
							key={index}
							width="100%"
							pt="100%"
							borderRadius="md"
							boxShadow="lg"
						/>
					))}
				</Box>
			) : (
				<Box
					display="grid"
					gridTemplateColumns="repeat(3, 1fr)"
					gap={4}
				>
					{media.videos.map((video, index) => (
						<Box
							key={index}
							width="100%"
							pt="100%"
							position="relative"
							cursor="pointer"
							borderRadius="md"
							overflow="hidden"
							boxShadow="lg"
							onClick={() => setOverlayMedia(video)}
						>
							<Box
								position="absolute"
								top="0"
								left="0"
								width="100%"
								height="100%"
								display="flex"
								alignItems="center"
								justifyContent="center"
							>
								<video
									width="100%"
									height="100%"
									style={{ borderRadius: '8px', objectFit: 'cover' }}
								>
									<source
										src={video.displayUrl + '#t=0.001'} // Use display file URL | #t=0.001 tells browser to skip first milliseconds of the video for preload
										type={video.displayUrl.toLowerCase().endsWith('.mov') ? 'video/quicktime' : 'video/mp4'}
									//Your browser does not support the video tag.
									/>
								</video>
							</Box>
						</Box>
					))}
				</Box>
			)}


			{showUpload && isEditable && (
				isUploading ? (
					<Spinner
						size="md"
						color={spinnerColor}
						mt={4}
						speed='0.65s'
					//emptyColor='gray.200'
					/>

				) : (
					<Button
						colorScheme="blue"
						mt={4}
						bgGradient='linear(to-r, #FF0080, #7928CA)'
						textColor={'white'}
						_hover={{ bg: 'green.500', bgGradient: 'linear(to-r, red.500, purple.500)' }}
						onClick={handleUpload}
					>
						Upload Media
					</Button>)
			)}

			{/* Overlay for Preview */}
			{overlayMedia && (
				<Modal isOpen={isOpen} onClose={handleClose} isCentered>
					<ModalOverlay />
					<ModalContent
						bg="blackAlpha.800"
						color="white"
						maxW="90vw"
						maxH="90vh"
						display="flex"
						justifyContent="center"
						alignItems="center"
					>
						<ModalCloseButton color="white" />
						<ModalBody p={4} display="flex" justifyContent="center" alignItems="center">
							{overlayMedia.contentType && overlayMedia.contentType.startsWith('video') ? (
								<video
									src={overlayMedia.displayUrl + '#t=0.001'}
									controls
									style={{
										width: '100%',
										height: '100%',
										maxWidth: '80vw',
										maxHeight: '80vh',
										borderRadius: '8px',
									}}
								/>
							) : (
								<Image
									src={overlayMedia.compressedUrl}
									alt="Preview"
									maxW="80vw"
									maxH="80vh"
									borderRadius="8px"
								/>
							)}
						</ModalBody>
						<Box textAlign="center" py={4}>
							{!isUserPage && ( // Hide the button if the URL contains "/user"
								<Button
									colorScheme="blue"
									onClick={() =>
										handleDownload(
											overlayMedia.url,
											overlayMedia.fileName,
											overlayMedia.contentType || 'media'
										)
									}
									bgGradient="linear(to-r, #FF0080, #7928CA)"
									textColor="white"
									_hover={{ bg: 'green.500', bgGradient: 'linear(to-r, red.500, purple.500)' }}
								>
									Download
								</Button>
							)}
						</Box>
					</ModalContent>
				</Modal>
			)}
		</Box>
	);
};

export default MediaComponent;
